import { ObjectId } from 'mongodb';
import { MEDIA_TYPE, MessageContentsType, MESSAGE_DIRECTION, MESSAGE_STATUS } from './constants';
import { CHAT_STATUS } from '../chat/constants';
import { IChatChannelInfo } from '../chat';
import { WhatsApp, Messenger, Instagram } from '../channel';
import { isObject } from '../utils';
import { Email } from '../channel/email';

export interface IMessageChannelInfo {
    /** ID of message from channel/provider */
    id: string;
    /** Any channel-specific metadata */
    metadata?: {
        [x: string]: unknown;
    };
}

export interface IMessageMedia<IsJSON extends boolean = false> {
    caption?: string;
    /** File name on sender device */
    filename?: string;
    /** Mimetype of the file */
    contentType?: string;
    /** File media type */
    mediaType: MEDIA_TYPE;
    /** File ID */
    fileId?: IsJSON extends true ? string : ObjectId;
    previewId?: IsJSON extends true ? string : ObjectId;
    /**
     * @deprecated Consider removing in v7+
     */
    url?: string;
    /**
     * Is the file scanned for threats?
     */
    scanned?: boolean;
    /** Any channel-specific metadata */
    metadata?: {
        [x: string]: unknown;
    };
}

export interface ContactCard {
    name: {
        formatted_name: string;
        first_name?: string;
        last_name?: string;
        middle_name?: string;
        prefix?: string;
        suffix?: string;
        /** Any channel/app-specific additional custom fileds */
        [x: string]: unknown;
    };
    phones?: Array<{
        phone: string;
        type?: string;
        /** Any channel/app-specific additional custom fileds */
        [x: string]: unknown;
    }>;
    emails?: Array<{
        email: string;
        type?: string;
        /** Any channel/app-specific additional custom fileds */
        [x: string]: unknown;
    }>;
    urls?: Array<{
        url: string;
        /** Any channel/app-specific additional custom fileds */
        [x: string]: unknown;
    }>;
    addresses?: Array<{
        /** Optional. Street number and name. */
        street?: string;
        /** Optional. City name. */
        city?: string;
        /** Optional. State abbreviation. */
        state?: string;
        /** Optional. ZIP code. */
        zip?: string;
        /** Optional. Full country name. */
        country?: string;
        /** Optional. Two-letter country abbreviation. */
        country_code?: string;
        /** Optional. Standard values are HOME and WORK. */
        type?: string;
    }>;
    org?: {
        /** Name of the contact's company */
        company?: string;
        /** Name of the contact's department. */
        department?: string;
        /** Contact's business title. */
        title?: string;
    };
    /** YYYY-MM-DD formatted string. */
    birthday?: string;
}

export interface LocationData {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
    url?: string;
}

export interface MessageCommon<IsJSON extends boolean = false> {
    _id: IsJSON extends true ? string : ObjectId;
    chatChannelInfo: IChatChannelInfo;
    parent_chat: IsJSON extends true ? string : ObjectId;
    /**
     * Message received time in **Milliseconds**
     * @deprecated use "timestamp" property
     */
    received: number;
    /** Message created time in **Milliseconds */
    timestamp: number;
    updatedAt?: IsJSON extends true ? string : Date;
    botSessionId?: string;
}

export interface SystemMessage<IsJSON extends boolean = false> extends MessageCommon<IsJSON> {
    systemMessage: true;
    newChatStatus: CHAT_STATUS;
    agent?: string;
    assignedToAgent?: string;
    assignedToDepartment?: string;
    /**
     * Added to system messages only because
     * the same collection used with regular messages,
     * and channelInfo.id field needed for mandatory full unique index
     */
    channelInfo: {
        /** This value have no meaning except it just unique */
        id: string;
    };
}

export type RegularMessage<IsJSON extends boolean = false, Special = MessageSpecial> = MessageCommon<IsJSON> & {
    systemMessage?: false;
    channelInfo: IMessageChannelInfo;
    direction: MESSAGE_DIRECTION;
    status: MESSAGE_STATUS;
    statusHistory: Array<{
        timestamp: number;
        status: MESSAGE_STATUS;
    }>;
    reactions?: string[];
    reactionsHistory?: Array<{
        timestamp: number;
        reaction?: string;
        removed?: true;
    }>;
    tmpSendId?: string;
    agent?: string;
    hideSummary?: boolean;
    errorReason?: string;
    text?: string;
    metadata?: {
        /** Bulk sending ID */
        bulkSendId?: IsJSON extends true ? string : ObjectId;
        botMsgInfo?: MessageBotInfo;
        /**
         * @deprecated Use metadata.whatsappTemplate
         */
        templateParameters?: string[];
        /**
         * @deprecated Use metadata.whatsappTemplate
         */
        templateMessage?: boolean;
        /**
         * @deprecated Use metadata.whatsappTemplate
         */
        templateMessageData?: MessageTemplateData;
        whatsappTemplate?: WhatsAppMessageTemplateData;
        [x: string]: unknown;
    };
    /**
     * @deprecated Use metadata.bulkSendId
     */
    bulkSendId?: IsJSON extends true ? string : ObjectId;
    /**
     * @deprecated Use metadata.botMsgInfo
     */
    botMsgInfo?: MessageBotInfo;
    /**
     * @deprecated use "direction" property instead
     */
    incoming?: boolean;
    /**
     * @deprecated use "direction" property instead
     */
    outgoing?: boolean;
    /**
     * @deprecated In any new code use channelInfo.id
     */
    providerMessageId?: string;
    /** @deprecated */
    messageId?: string;
    /** @deprecated */
    templateId?: string;
    /**
     * @deprecated Use metadata.templateParameters
     */
    parameters?: string[];
    /**
     * @deprecated Use metadata.templateMessage
     */
    templateMessage?: boolean;
    /**
     * @deprecated Use metadata.templateMessageData
     */
    templateMessageData?: MessageTemplateData;
} & MessageContents<Special, IsJSON>;

export type MessageBotInfo = {
    nodeName: string;
    hideSummary: boolean;
    identifier?: string;
    version?: number;
};

export function isRegularMessage<IsJSON extends boolean = true>(message: unknown): message is RegularMessage<IsJSON> {
    return isObject(message)
        && message
        && !!message.direction;
}

export function isSystemMessage<IsJSON extends boolean = true>(message: unknown): message is SystemMessage<IsJSON> {
    return isObject(message)
        && message
        && typeof message.systemMessage === 'boolean'
        && message.systemMessage;
}

/**
 * @deprecated Use WhatsAppMessageTemplateData
 */
type MessageTemplateData = {
    id: string;
    /** @todo proper type */
    header?: unknown;
    body: {
        text: string;
    };
    /** @todo proper type */
    footer?: unknown;
    /** @todo proper type */
    buttons?: unknown;
};

type WhatsAppMessageTemplateData = {
    name: string;
    localization: WhatsApp.TemplateLocalization;
    contents: WhatsApp.TemplateSendContents;
};

export type IMessage<IsJSON extends boolean = false, WithContext extends boolean = false> = SystemMessage<IsJSON> | (
    WithContext extends false ? RegularMessage<IsJSON> : MessageWithContext<IsJSON>
);

export type MessageWithContext<IsJSON extends boolean = false> = Omit<RegularMessage<IsJSON>, 'context'> & {
    context?: RegularMessage<IsJSON>;
};

export type MessageContents<Special = MessageSpecial, IsJSON extends boolean = false> = {
    special?: Special;
    metadata?: MessageMetadata;
    /**
     * If `TRUE` - message is sensitive and should be protected
     */
    sensitive?: boolean;
} & (
    MessageSpecialContents<Special> | MessageTextContents | MessageMediaContents<IsJSON> | MessageContactsContents
    | MessageLocationContents | MessageButtonsContents | MessageListContents
    | MessagePostbackContents
);

export type MessageMetadata = {
    [x: string]: unknown;
};

export type MessageContentsBase = {
    /** Contextual message info if this is reply */
    context?: IMessageChannelInfo;
    forwarded?: boolean;
    /**
     * Client-generated string to match sending requests with created messages.
     * Not required to be globally unique
     */
    tmpSendId?: string;
};

export type MessageTextContents = MessageContentsBase & {
    type: MessageContentsType.text;
    text: string;
};

export function isTextMessage(message: unknown): message is MessageTextContents {
    return isObject(message) && message.type === MessageContentsType.text;
}

export type MessageMediaContents<IsJSON extends boolean = false> = MessageContentsBase & {
    type: MessageContentsType.media;
    media: IMessageMedia<IsJSON>[];
};

export function isMediaMessage<IsJSON extends boolean = true>(message: unknown): message is MessageMediaContents<IsJSON> {
    return isObject(message) && message.type === MessageContentsType.media;
}

export type MessageContactsContents = MessageContentsBase & {
    type: MessageContentsType.contacts;
    contacts: ContactCard[];
};

export function isContactsMessage(message: unknown): message is MessageContactsContents {
    return isObject(message) && message.type === MessageContentsType.contacts;
}

export type MessageLocationContents = MessageContentsBase & {
    type: MessageContentsType.location;
    location: LocationData;
};

export function isLocationMessage(message: unknown): message is MessageLocationContents {
    return isObject(message) && message.type === MessageContentsType.location;
}

export type MessageButtonsContents = MessageContentsBase & {
    type: MessageContentsType.buttons;
    text: string;
    buttons: MessagePostbackButton[];
};

export function isButtonsMessage(message: unknown): message is MessageButtonsContents {
    return isObject(message) && message.type === MessageContentsType.buttons;
}

export type MessageListContents = MessageContentsBase & {
    type: MessageContentsType.list;
    text: string;
    list: {
        /** Title of the button to open list when it hidden. Required by some channels and by some not used */
        button: string;
        sections: MessageListSection[];
    };
};

export function isListMessage(message: unknown): message is MessageListContents {
    return isObject(message) && message.type === MessageContentsType.list;
}

export type MessagePostbackContents = MessageContentsBase & {
    type: MessageContentsType.postback;
    postback: {
        /** Interactive button payload/ID */
        payload: string;
        /** Interactive button title */
        title?: string;
        /** Interactive button description */
        description?: string;
    };
};

export function isPostbackMessage(message: unknown): message is MessagePostbackContents {
    return isObject(message) && message.type === MessageContentsType.postback;
}

export type MessageSpecialContents<Special = MessageSpecial> = MessageContentsBase & {
    type: MessageContentsType.special;
    text?: string;
    special: Special;
};

export function isSpecialMessage(message: unknown): message is MessageSpecialContents {
    return isObject(message) && message.type === MessageContentsType.special;
}

export interface MessageListSection {
    title: string;
    rows: Array<MessagePostbackButton & { description?: string }>;
}

export interface MessagePostbackButton {
    title: string;
    payload: string;
}

export interface MessageSpecial {
    messenger?: Messenger.Message.Special;
    whatsapp?: WhatsApp.MessageSpecialContents;
    instagram?: Instagram.Message.Special;
    /**
     * Message isn't actual user interaction message, but some system information update.
     */
    isStatusMessage?: true;
    email?: Email.Message.Special;
    /**
     * Message contents was redacted due to sensitive information or other reasons.
     */
    redacted?: true;
    /**
     * Message type unsupported by external API of the provider.
     */
    unsupportedByProvider?: true;
    [x: string]: unknown;
}
